import React, { useState, useEffect } from 'react';
import { X, Save, Music } from 'lucide-react';

const CLASS_TYPES = ['Private', 'SmallGroup', 'Group'];

const CLASS_TYPE_LABELS = {
  Private:    'Private (1-on-1)',
  SmallGroup: 'Small Group (3–5)',
  Group:      'Group (up to 10–15)'
};

const ArtSchoolClassModal = ({ cls, categories, teachers, academicYear, onClose, onSave }) => {
  const [form, setForm] = useState({
    categoryID: '',
    className: '',
    classType: 'Private',
    teacherID: '',
    schedule: '',
    dayOfWeek: '',
    payoutPerStudentPrivate: '',
    payoutPerStudentGroup: '',
    basePayoutAmount: '',
    basePayoutStudents: 5,
    additionalStudentRate: 1.50,
    maxStudents: '',
    notes: '',
    isActive: 1
  });
  const [saving, setSaving] = useState(false);

  // Preview pricing from selected category + classType
  const [pricingPreview, setPricingPreview] = useState(null);

  useEffect(() => {
    if (cls) {
      setForm({
        categoryID:              cls.CategoryID              || '',
        className:               cls.ClassName               || '',
        classType:               cls.ClassType               || 'Private',
        teacherID:               cls.TeacherID               || '',
        schedule:                cls.Schedule                || '',
        dayOfWeek:               cls.DayOfWeek               || '',
        payoutPerStudentPrivate: cls.PayoutPerStudentPrivate ?? '',
        payoutPerStudentGroup:   cls.PayoutPerStudentGroup   ?? '',
        basePayoutAmount:        cls.BasePayoutAmount        ?? '',
        basePayoutStudents:      cls.BasePayoutStudents      ?? 5,
        additionalStudentRate:   cls.AdditionalStudentRate   ?? 1.50,
        maxStudents:             cls.MaxStudents             || '',
        notes:                   cls.Notes                   || '',
        isActive:                cls.IsActive                ?? 1
      });
    }
  }, [cls]);

  // Update pricing preview when category or classType changes
  useEffect(() => {
    if (!form.categoryID) { setPricingPreview(null); return; }
    const cat = categories.find(c => c.CategoryID === parseInt(form.categoryID));
    if (!cat) { setPricingPreview(null); return; }
    const type = form.classType;
    const monthly = type === 'Private'    ? cat.PrivateMonthly
                  : type === 'SmallGroup' ? cat.SmallGroupMonthly
                  : cat.GroupMonthly;
    const threeMonth = type === 'Private'    ? cat.Private3Month
                     : type === 'SmallGroup' ? cat.SmallGroup3Month
                     : cat.Group3Month;
    setPricingPreview(monthly || threeMonth ? { monthly, threeMonth } : null);
  }, [form.categoryID, form.classType, categories]);

  const set = (field, val) => setForm(f => ({ ...f, [field]: val }));

  // Determine category type for payout field display
  const selectedCategory = categories.find(c => c.CategoryID === parseInt(form.categoryID));
  const isMusic = selectedCategory?.CategoryName?.toLowerCase().includes('music');

  const handleSave = async () => {
    if (!form.categoryID || !form.className.trim() || !form.classType) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        academicYear,
        // For non-music: clear music-specific fields
        payoutPerStudentPrivate: isMusic ? (form.payoutPerStudentPrivate || null) : null,
        payoutPerStudentGroup:   isMusic ? (form.payoutPerStudentGroup   || null) : null,
        // For music: clear base payout fields
        basePayoutAmount:       !isMusic ? (form.basePayoutAmount       || null) : null,
        basePayoutStudents:     !isMusic ? (form.basePayoutStudents     || 5)    : null,
        additionalStudentRate:  !isMusic ? (form.additionalStudentRate  || 1.50) : null,
      };
      await onSave(payload);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Music size={20} className="text-indigo-600" />
            </div>
            <h2 className="text-lg font-bold text-gray-800">
              {cls ? 'Edit Class' : 'New Class'}
            </h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
              value={form.categoryID}
              onChange={e => set('categoryID', e.target.value)}
            >
              <option value="">Select category...</option>
              {categories.map(c => (
                <option key={c.CategoryID} value={c.CategoryID}>{c.CategoryName}</option>
              ))}
            </select>
          </div>

          {/* Class Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Class Name *</label>
            <input
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
              value={form.className}
              onChange={e => set('className', e.target.value)}
              placeholder="e.g. Drums, Ballet, Painting..."
            />
          </div>

          {/* Class Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Class Type *</label>
            <div className="grid grid-cols-3 gap-2">
              {CLASS_TYPES.map(type => (
                <button
                  key={type}
                  onClick={() => set('classType', type)}
                  className={`py-2 px-3 rounded-lg border text-sm font-medium transition-colors
                    ${form.classType === type
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400'}`}
                >
                  {CLASS_TYPE_LABELS[type]}
                </button>
              ))}
            </div>
          </div>

          {/* Pricing Preview */}
          {pricingPreview && (
            <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-100">
              <p className="text-xs font-medium text-indigo-700 mb-1">Pricing from category</p>
              <div className="flex gap-4 text-sm text-indigo-800">
                {pricingPreview.monthly    && <span>Monthly: <strong>${pricingPreview.monthly}</strong></span>}
                {pricingPreview.threeMonth && <span>3-Month: <strong>${pricingPreview.threeMonth}</strong></span>}
              </div>
            </div>
          )}
          {form.categoryID && !pricingPreview && (
            <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-200">
              <p className="text-xs text-yellow-700">
                No pricing defined for this class type in the selected category.
              </p>
            </div>
          )}

          {/* Teacher */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Teacher</label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
              value={form.teacherID}
              onChange={e => set('teacherID', e.target.value)}
            >
              <option value="">Unassigned</option>
              {teachers.map(t => (
                <option key={t.TeacherID} value={t.TeacherID}>
                  {t.FirstName} {t.LastName}
                </option>
              ))}
            </select>
          </div>

          {/* Schedule */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Day of Week</label>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                value={form.dayOfWeek}
                onChange={e => set('dayOfWeek', e.target.value)}
              >
                <option value="">— Select day —</option>
                {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Time / Schedule</label>
              <input
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                value={form.schedule}
                onChange={e => set('schedule', e.target.value)}
                placeholder="e.g. 4:00–5:00 PM"
              />
            </div>
          </div>

          {/* Teacher Payout — category-aware */}
          {form.categoryID && (
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <p className="text-sm font-semibold text-gray-700 mb-3">Teacher Payout (per session)</p>
              {isMusic ? (
                // Music: flat rate per session
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Private — rate per student ($)
                    </label>
                    <input
                      type="number" min="0" step="0.01"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                      value={form.payoutPerStudentPrivate}
                      onChange={e => set('payoutPerStudentPrivate', e.target.value)}
                      placeholder="15.00"
                    />
                    <p className="text-xs text-gray-400 mt-1">× students × sessions/month</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Group — flat rate per session ($)
                    </label>
                    <input
                      type="number" min="0" step="0.01"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                      value={form.payoutPerStudentGroup}
                      onChange={e => set('payoutPerStudentGroup', e.target.value)}
                      placeholder="30.00"
                    />
                    <p className="text-xs text-gray-400 mt-1">× sessions/month (flat)</p>
                  </div>
                </div>
              ) : (
                // Dance / Fine Arts / Digital Innovation: base + additional
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Base amount per session ($)
                      </label>
                      <input
                        type="number" min="0" step="0.01"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                        value={form.basePayoutAmount}
                        onChange={e => set('basePayoutAmount', e.target.value)}
                        placeholder="30.00"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Covers up to (students)
                      </label>
                      <input
                        type="number" min="1"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                        value={form.basePayoutStudents}
                        onChange={e => set('basePayoutStudents', e.target.value)}
                        placeholder="5"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Rate per additional student above threshold ($)
                    </label>
                    <input
                      type="number" min="0" step="0.01"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                      value={form.additionalStudentRate}
                      onChange={e => set('additionalStudentRate', e.target.value)}
                      placeholder="1.50"
                    />
                  </div>
                  <div className="text-xs text-gray-400 bg-white border border-gray-100 rounded p-2">
                    Example: {form.basePayoutAmount || '30'}$ base for {form.basePayoutStudents || 5} students,
                    +{form.additionalStudentRate || '1.50'}$ per extra student, × sessions/month
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Max Students */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max Students (optional)</label>
            <input
              type="number"
              min="1"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
              value={form.maxStudents}
              onChange={e => set('maxStudents', e.target.value)}
              placeholder="Leave blank for no limit"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
              rows={2}
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
              placeholder="Any notes..."
            />
          </div>

          {cls && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="classActive"
                checked={form.isActive === 1}
                onChange={e => set('isActive', e.target.checked ? 1 : 0)}
                className="w-4 h-4 rounded text-indigo-600"
              />
              <label htmlFor="classActive" className="text-sm text-gray-700">Active</label>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-5 border-t bg-gray-50 rounded-b-xl sticky bottom-0">
          <button onClick={onClose} className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !form.categoryID || !form.className.trim()}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            <Save size={16} />
            {saving ? 'Saving...' : 'Save Class'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ArtSchoolClassModal;
